import {
  Ban,
  KeyRound,
  MailCheck,
  MoreHorizontal,
  Pencil,
  Shield,
  Trash2,
  UserCheck,
  UserX,
} from "lucide-react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import {
  ColocaDropdownMenu,
  type ColocaDropdownGroup,
  type ColocaDropdownItem,
} from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";

import { UserStatus } from "../types/create-user.types";
import type { UserListItem } from "../types/users-api.types";
import { isSelfLockoutStatus } from "../utils/selfLockout";

export interface UserRowActionsProps {
  user: UserListItem;
  isCurrentUser: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canManageRoles: boolean;
  onOpenDetail: () => void;
  onManageAccess: () => void;
  onVerifyEmail: () => void;
  onSetStatus: (status: UserStatus) => void;
  onSetPassword: () => void;
  onDelete: () => void;
}

/**
 * Per-row actions.
 *
 * A permission the operator lacks makes an item **absent**, not disabled — a greyed-out row action is a
 * promise the API will not keep. Two rules that are not cosmetic:
 *
 * - **Verify email only appears when the address is unverified.** The endpoint has no idempotency check: it
 *   re-stamps the timestamp and forces `status = active`, so offering it on an already-verified row would be
 *   a button that silently reactivates a suspended account.
 * - **Nothing that locks you out is offered on your own row.** Delete, Suspend and Deactivate are all absent
 *   there, because the backend has no self-mutation guard of any kind — not on `DELETE`, and not on the
 *   `status` field of `PATCH /internal/users/:id` — so the client is the only thing standing between an
 *   operator and locking themselves out. Activate is exempt: it is the direction that restores access. See
 *   `isSelfLockoutStatus`.
 */
export function UserRowActions({
  user,
  isCurrentUser,
  canWrite,
  canDelete,
  canManageRoles,
  onOpenDetail,
  onManageAccess,
  onVerifyEmail,
  onSetStatus,
  onSetPassword,
  onDelete,
}: UserRowActionsProps) {
  const { t } = useTranslations("users");

  /** Would this transition end the operator's OWN session? Only ever true on your own row. */
  const blocksOwnAccess = (next: UserStatus) => isCurrentUser && isSelfLockoutStatus(next);

  const primary: ColocaDropdownItem[] = [{ label: t("action_view"), icon: Pencil, onClick: onOpenDetail }];

  if (canManageRoles) {
    primary.push({ label: t("action_manage_access"), icon: Shield, onClick: onManageAccess });
  }

  // Verification state is NOT the only guard. `POST /:id/verify-email` unconditionally forces
  // `status = ACTIVE` alongside the timestamp, so offering it on a suspended or deactivated row would be a
  // menu item that quietly lifts the suspension — with the row still reading "Suspendido" beside it.
  const isLockedOutByStatus = user.status === UserStatus.SUSPENDED || user.status === UserStatus.INACTIVE;
  if (canWrite && user.email_verified_at === null && !isLockedOutByStatus) {
    primary.push({ label: t("action_verify_email"), icon: MailCheck, onClick: onVerifyEmail });
  }

  if (canWrite) {
    // `ACTIVE` is the one transition that is safe on your own row — it restores access rather than removing
    // it, so it is not filtered by `isSelfLockoutStatus`.
    if (user.status !== UserStatus.ACTIVE) {
      primary.push({
        label: t("action_activate"),
        icon: UserCheck,
        onClick: () => onSetStatus(UserStatus.ACTIVE),
      });
    }
    if (user.status === UserStatus.ACTIVE && !blocksOwnAccess(UserStatus.INACTIVE)) {
      primary.push({
        label: t("action_deactivate"),
        icon: UserX,
        onClick: () => onSetStatus(UserStatus.INACTIVE),
      });
    }
    primary.push({ label: t("action_set_password"), icon: KeyRound, onClick: onSetPassword });
  }

  const destructive: ColocaDropdownItem[] = [];
  if (canWrite && user.status !== UserStatus.SUSPENDED && !blocksOwnAccess(UserStatus.SUSPENDED)) {
    destructive.push({
      label: t("action_suspend"),
      icon: Ban,
      variant: "destructive",
      onClick: () => onSetStatus(UserStatus.SUSPENDED),
    });
  }
  if (canDelete && !isCurrentUser) {
    destructive.push({ label: t("action_delete"), icon: Trash2, variant: "destructive", onClick: onDelete });
  }

  const groups: ColocaDropdownGroup[] = [{ items: primary }];
  if (destructive.length > 0) groups.push({ items: destructive });

  return (
    <ColocaDropdownMenu
      align="end"
      groups={groups}
      trigger={
        <ColocaIconButton
          icon={MoreHorizontal}
          variant="ghost"
          size="sm"
          aria-label={t("row_actions_label", { name: user.full_name })}
        />
      }
    />
  );
}
